import { Box, Tooltip, TextField, InputAdornment } from '@mui/material'
import {
  Toolbar,
  ToolbarButton,
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  ExportCsv,
  QuickFilter,
  QuickFilterControl,
  QuickFilterClear,
} from '@mui/x-data-grid'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import FilterListIcon from '@mui/icons-material/FilterList'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import SearchIcon from '@mui/icons-material/Search'
import CancelIcon from '@mui/icons-material/Cancel'

interface DataGridToolbarProps {
  /** Custom filename for CSV export (without .csv extension) */
  fileName?: string
  /** Show/hide the quick search filter */
  showQuickFilter?: boolean
  /** Show/hide the columns visibility button */
  showColumnsButton?: boolean
  /** Show/hide the filter button */
  showFilterButton?: boolean
  /** Show/hide the export button (CSV only) */
  showExport?: boolean
}

/**
 * Reusable DataGrid toolbar component for consistent table functionality across the app.
 *
 * Features:
 * - Column visibility toggle
 * - Advanced filtering
 * - CSV export
 * - Quick search filter
 *
 * @example
 * // Basic usage with default settings
 * <DataGrid slots={{ toolbar: () => <DataGridToolbar fileName="my-data" /> }} showToolbar />
 *
 * @example
 * // Minimal toolbar with only export and search
 * <DataGrid slots={{ toolbar: () => (
 *   <DataGridToolbar
 *     fileName="user-export"
 *     showColumnsButton={false}
 *     showFilterButton={false}
 *   />
 * )}} showToolbar />
 */
export default function DataGridToolbar({
  fileName = 'data-export',
  showQuickFilter = true,
  showColumnsButton = true,
  showFilterButton = true,
  showExport = true,
}: DataGridToolbarProps) {
  return (
    <Toolbar>
      {showColumnsButton && (
        <Tooltip title="Columns">
          <ColumnsPanelTrigger render={<ToolbarButton />}>
            <ViewColumnIcon fontSize="small" />
          </ColumnsPanelTrigger>
        </Tooltip>
      )}
      {showFilterButton && (
        <Tooltip title="Filters">
          <FilterPanelTrigger render={<ToolbarButton />}>
            <FilterListIcon fontSize="small" />
          </FilterPanelTrigger>
        </Tooltip>
      )}
      {showExport && (
        <Tooltip title="Export">
          <ExportCsv
            render={<ToolbarButton />}
            options={{
              fileName,
              delimiter: ',',
              utf8WithBom: true,
            }}
          >
            <FileDownloadIcon fontSize="small" />
          </ExportCsv>
        </Tooltip>
      )}
      <Box sx={{ flexGrow: 1 }} />
      {showQuickFilter && (
        <QuickFilter expanded>
          <QuickFilterControl
            render={(controlProps, state) => (
              <TextField
                value={controlProps.value}
                onChange={controlProps.onChange}
                onBlur={controlProps.onBlur}
                inputRef={controlProps.ref}
                aria-label="Search"
                placeholder="Search..."
                size="small"
                sx={{ width: 260 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: state.value ? (
                      <InputAdornment position="end">
                        <QuickFilterClear
                          edge="end"
                          size="small"
                          aria-label="Clear search"
                          material={{ sx: { marginRight: -0.75 } }}
                        >
                          <CancelIcon fontSize="small" />
                        </QuickFilterClear>
                      </InputAdornment>
                    ) : null,
                    ...controlProps.slotProps?.input,
                  },
                  ...controlProps.slotProps,
                }}
              />
            )}
          />
        </QuickFilter>
      )}
    </Toolbar>
  )
}
